/**
 * Shared resolver helpers for input validation and array filtering.
 * Used to consistently guard inputs and filter malformed elements
 * from CMS data arrays.
 */

import { isNavigableUrl } from './safeUrl';

/**
 * Returns true if the input is a non-null plain object (not an array, not a function).
 * Used as the first check in every resolver to reject invalid Data_Object inputs.
 */
export function isValidDataObject(input: unknown): input is Record<string, unknown> {
  return (
    typeof input === 'object' &&
    input !== null &&
    !Array.isArray(input) &&
    typeof input !== 'function'
  );
}

/**
 * Filters an array to only include elements that have all specified
 * required fields present and non-empty (not null, undefined, or empty string).
 * Returns an empty array if the input is not an array.
 *
 * Note: falsy values like `0` and `false` are considered valid (only null,
 * undefined, and empty string are treated as "empty").
 */
export function filterValidElements<T>(
  arr: unknown,
  requiredFields: (keyof T)[]
): T[] {
  if (!Array.isArray(arr)) {
    return [];
  }

  return arr.filter((element): element is T => {
    if (typeof element !== 'object' || element === null) {
      return false;
    }

    return requiredFields.every((field) => {
      const value = (element as Record<string, unknown>)[field as string];
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === 'string' && value === '') {
        return false;
      }
      return true;
    });
  });
}

/**
 * Copies only the listed keys from a Data object, skipping keys whose value is undefined.
 * Every other key is dropped, so a resolver never forwards an unknown Data field to a
 * component that spreads its remaining props onto the DOM.
 *
 * @param source - Data object, already checked with isValidDataObject.
 * @param keys - The component prop names to copy.
 * @returns A partial props object holding only the listed keys.
 */
export function pickKnownKeys<T>(
  source: Record<string, unknown>,
  keys: ReadonlyArray<keyof T>
): Partial<T> {
  const picked: Partial<T> = {};
  for (const key of keys) {
    const value = source[key as string];
    if (value !== undefined) {
      picked[key] = value as T[keyof T];
    }
  }
  return picked;
}

/**
 * Returns a copy of a Data object without the listed URL keys whose value is not a navigable
 * href (isNavigableUrl). A failing URL is treated exactly as an absent one, so the caller's
 * existing absent-field rules decide what happens next.
 *
 * @param source - Data object, already checked with isValidDataObject.
 * @param urlKeys - Keys that hold an authored href.
 * @returns A shallow copy with failing URL keys removed.
 */
export function omitUnsafeUrls(
  source: Record<string, unknown>,
  urlKeys: ReadonlyArray<string>
): Record<string, unknown> {
  const copy: Record<string, unknown> = { ...source };
  for (const key of urlKeys) {
    const value = copy[key];
    if (value !== undefined && !(typeof value === 'string' && isNavigableUrl(value))) {
      delete copy[key];
    }
  }
  return copy;
}

/** Characters that end or escape an unquoted CSS `url(...)` token, so an authored value could append a declaration. */
const CSS_URL_BREAKOUT = /[()'"\\\s\u0000-\u001f\u007f-\u009f]/;

/**
 * Returns a copy of a Data object without the listed keys whose value cannot be placed inside an
 * inline `url(...)`: the value must be a navigable URL (isNavigableUrl) and contain no parenthesis,
 * quote, backslash, whitespace or control character. A failing value is treated as absent.
 *
 * @param source - Data object, already checked with isValidDataObject.
 * @param cssUrlKeys - Keys whose value a component writes into a `backgroundImage: url(...)` style.
 * @returns A shallow copy with failing keys removed.
 */
export function omitUnsafeCssUrls(
  source: Record<string, unknown>,
  cssUrlKeys: ReadonlyArray<string>
): Record<string, unknown> {
  const copy: Record<string, unknown> = { ...source };
  for (const key of cssUrlKeys) {
    const value = copy[key];
    if (value !== undefined
      && !(typeof value === 'string' && isNavigableUrl(value) && !CSS_URL_BREAKOUT.test(value))) {
      delete copy[key];
    }
  }
  return copy;
}

/**
 * Returns a copy of a Data object whose listed keys hold real booleans, with the flat path's
 * getBoolField semantics: a boolean is kept, `'true'` and `'false'` are converted, and any
 * other defined value is treated as absent and removed.
 *
 * @param source - Data object, already checked with isValidDataObject.
 * @param booleanKeys - Keys whose component prop is a boolean.
 * @returns A shallow copy with the listed keys coerced or removed.
 */
export function coerceBooleanKeys(
  source: Record<string, unknown>,
  booleanKeys: ReadonlyArray<string>
): Record<string, unknown> {
  const copy: Record<string, unknown> = { ...source };
  for (const key of booleanKeys) {
    const value = copy[key];
    if (value === 'true') {
      copy[key] = true;
    } else if (value === 'false') {
      copy[key] = false;
    } else if (value !== undefined && typeof value !== 'boolean') {
      delete copy[key];
    }
  }
  return copy;
}

/**
 * Returns a copy of a Data object without the listed keys whose defined value is not a string.
 * An object or array in a text field would otherwise reach React as a child and throw during
 * render, so a non-string is treated exactly as an absent value and the caller's existing
 * absent-field rules decide what happens next.
 *
 * @param source - Data object, already checked with isValidDataObject.
 * @param stringKeys - Keys whose component prop is a string.
 * @returns A shallow copy with non-string values of the listed keys removed.
 */
export function omitNonStrings(
  source: Record<string, unknown>,
  stringKeys: ReadonlyArray<string>
): Record<string, unknown> {
  const copy: Record<string, unknown> = { ...source };
  for (const key of stringKeys) {
    const value = copy[key];
    if (value !== undefined && typeof value !== 'string') {
      delete copy[key];
    }
  }
  return copy;
}

/**
 * Returns a copy of a Data object without the listed keys whose defined value is not a finite
 * number. A string or object there would otherwise be built into an inline style, so a
 * non-number is treated exactly as an absent value.
 *
 * @param source - Data object, already checked with isValidDataObject.
 * @param numberKeys - Keys whose component prop is a number.
 * @returns A shallow copy with non-finite or non-number values of the listed keys removed.
 */
export function omitNonFiniteNumbers(
  source: Record<string, unknown>,
  numberKeys: ReadonlyArray<string>
): Record<string, unknown> {
  const copy: Record<string, unknown> = { ...source };
  for (const key of numberKeys) {
    const value = copy[key];
    if (value !== undefined && !(typeof value === 'number' && Number.isFinite(value))) {
      delete copy[key];
    }
  }
  return copy;
}
